/* file description */
import { request, promiseCacher } from '@cui/lcp-tools';
import * as Apis from './page.api';
import { getMaxFloor } from '@/utils';

// 数据资产场景页面
export const getAssetsDataScenePage = async ({ logicCode, sceneCode }) => {
  const params = {
    sceneCode,
  };
  return request({
    url: `/api/gateway/metabase/compose/template/${logicCode}`,
    data: {
      params,
    },
  });
};

interface GetTaskPageType {
  (o: { taskId: string }): Promise<{
    maxTag?: number;
    relationTree: any;
    modules: Array<{
      moduleID: string;
      handle: string;
      fields: Array<{
        label: string;
        kind: string;
        name: string;
      }>;
    }>;
    mainModuleId: string;
    mainModuleCode: string;
  }>;
}

// 任务页面
export const getTaskPage: GetTaskPageType = async ({ taskId }) => {
  const taskInfo = await request({
    url: `/api/gateway/metabase/compose/task/template/${taskId}`,
  });
  taskInfo.maxTag = getMaxFloor(taskInfo?.relationTree || []);
  taskInfo.mainModuleCode = taskInfo?.modules?.find((m: any) => m.moduleID === taskInfo.mainModuleId)?.handle;
  return taskInfo;
};
export const getTaskPageCache: GetTaskPageType = promiseCacher(getTaskPage);

export const getTaskAdd = async ({ taskId, fieldIdList }) => {
  return request({
    method: 'post',
    url: `/api/gateway/metabase/compose/task/template/constraint/${taskId}`,
    data: fieldIdList,
  });
};

// 获取场景配置页
export const getPageLowCode = async ({ pageStatus, pageKey }) => {
  const pageStatusKey = pageStatus === 'edit' ? 'create' : pageStatus;

  const { schema } = await getPageDetail({
    task_id: pageKey,
    page_preset_type: pageStatusKey,
    conditionType: ' AND ',
  });
  const subPages = schema?.subPages ?? [];
  const comps = subPages?.reduce((acc, cur) => {
    return [
      ...acc,
      ...(cur?.components ?? []),
    ];
  }, []);
  const taskComp = comps?.find((comp) => {
    return comp.type === 'Lcp' && comp.taskId === pageKey && comp.pageType === pageStatusKey;
  });
  const config = taskComp?.config ?? {};

  return config;
};
export const getPageLowCodeCache = promiseCacher(getPageLowCode);

// 获取taskId下的所有数据
export const getTaskIdData = async ({ task_id, record_id, fieldName }: any) => {
  const res = await request({
    method: 'get',
    url: `/api/gateway/metabase/compose/task/${task_id}/record/list/${record_id}`,
    params: {
      recordKey: fieldName,
    },
  });

  return res;
};

export async function getTaskPropertySettings(
  taskId: string,
  idList: string[],
): Promise<any> {
  const res = await request({
    method: 'post',
    url: '/api/gateway/metabase/api/v1/tech/scene_activity_task_logic_entity_property/page',
    data: {
      condition: 'logic_entity_id in( ${idList}) and task_id=${taskId} ',
      fetchChildren: false,
      idToName: false,
      pageNum: 1,
      pageSize: 1000,
      parameters: {
        idList,
        taskId,
      },
      select: '',
      sort: '',
    },
  });
  return res?.data || {};
}
export const getTaskPropertySettingsCache = promiseCacher(getTaskPropertySettings);


export interface GetPageDetailType {
  (o: {
    route?: string;
    id?: string;
    task_id?: string;
    page_preset_type?: string;
    conditionType?: string;
  }): Promise<{
    pageDetail: Record<string, any>;
    pageVersionDetail: Record<string, any>;
    schema: Record<string, any>;
  }>;
}
export interface GetPageDetailByConditionType {
  (o: {
    page_route?: string;
    page_id?: string;
    task_id?: string;
    page_preset_type?: string;
    conditionType?: string;
  }): Promise<Record<string, any>>;
}

// 获取页面详情 + schema
export const getPageDetail: GetPageDetailType = async ({ route, id, task_id, page_preset_type, conditionType }) => {
  const pageDetail = await getPageDetailByCondition({
    page_route: route,
    page_id: id,
    task_id,
    page_preset_type,
    conditionType,
  });
  const { page_id, version } = pageDetail;
  const {
    pageVersionDetail,
  } = await getPageVersionDetail({ page_id, version });
  const schemaData = pageVersionDetail?.schema;
  let schema: Record<string, any> = {};
  try {
    schema = JSON.parse(schemaData ?? '{}');
  } catch {
    schema = {};
  }
  const pageVersionDetailData = _.omit(pageVersionDetail, 'schema');
  return {
    pageDetail,
    pageVersionDetail: pageVersionDetailData,
    schema,
  };
};

// 通过路由查页面
export const getPageDetailByCondition: GetPageDetailByConditionType = async ({
  page_route = undefined,
  page_id = undefined,
  task_id = undefined,
  page_preset_type = undefined,
  conditionType = ' OR ',
}) => {
  const conditions = [];
  page_route && conditions.push('page_route = ${page_route}');
  page_id && conditions.push('page_id = ${page_id}');
  task_id && conditions.push('task_id = ${task_id}');
  page_preset_type && conditions.push('page_preset_type = ${page_preset_type}');
  const condition = conditions.join(conditionType);

  const data = {
    condition,
    pageNum: 1,
    pageSize: 1000,
    parameters: {
      page_route,
      page_id,
      task_id,
      page_preset_type,
    },
  };
  const [, res] = await Apis.getPageList(data);
  const pageDetail = res?.data?.data?.[0] ?? {};
  return pageDetail;
};

// 通过页面版本查版本详情
export const getPageVersionDetail = async ({ page_id, version }) => {
  const data = {
    condition: 'page_id = ${page_id} AND version = ${version}',
    pageNum: 1,
    pageSize: 1000,
    parameters: {
      page_id,
      version,
    },
  };
  const [, res] = await Apis.getPageVersionList(data);
  const pageVersions = res?.data?.data ?? [];
  const pageVersionDetail = pageVersions?.find((item) => item.page_id === page_id && item.version === version);
  return {
    pageVersionDetail,
  };
};
